import { memo } from 'react';
import { Badge, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { NEW_BADGE_META } from '../../../constants';
import { BLOCKING_ISSUES, ISSUE, ROW_STATUS } from '../../../constants/reviewFilters.js';

/** Informative notes of a row (a suspended member being updated, a manager that is not in the workspace). */
function RowNotes({ notes }) {
  const { t } = useTranslation('people-access');
  if (!notes?.length) return null;
  return (
    <>
      {notes.map((note) => (
        <span key={note.code} className="text-xs text-content-secondary">
          {t(`import.review.notes.${note.code}`, { email: note.email })}
        </span>
      ))}
    </>
  );
}

/**
 * Status cell: New, Update (with a summary of what changes), or the red error labels. A label
 * may carry the quick fix of its issue: `Use <role>` for an unknown role or group,
 * `Remove manager` for a person that reports to themselves.
 *
 * @param {object} props
 * @param {object} props.row
 * @param {(row: object, issue: object) => void} props.onQuickFix
 */
function StatusCell({ row, onQuickFix }) {
  const { t } = useTranslation('people-access');

  if (row.status === ROW_STATUS.NEW) {
    return (
      <div className="flex flex-col items-start gap-1">
        <Badge size="small" variant={NEW_BADGE_META.variant}>
          {t(NEW_BADGE_META.labelKey)}
        </Badge>
        <RowNotes notes={row.notes} />
      </div>
    );
  }

  if (row.status === ROW_STATUS.UPDATE) {
    return (
      <div className="flex flex-col items-start gap-1">
        <Badge size="small" variant="informative">
          {t('import.review.status.update')}
        </Badge>
        <span className="text-xs text-content-secondary">
          {t('import.review.status.changes', {
            fields: row.changes.map((field) => t(`import.review.status.fields.${field}`)).join(', '),
          })}
        </span>
        <RowNotes notes={row.notes} />
      </div>
    );
  }

  return (
    <ul className="m-0 flex list-none flex-col items-start gap-1 p-0">
      {row.issues
        .filter((issue) => BLOCKING_ISSUES.includes(issue.code))
        .map((issue, index) => (
          <li key={`${issue.code}:${issue.value ?? ''}:${index}`} className="flex flex-wrap items-center gap-1">
            <Badge size="small" variant="danger">
              {t(`import.review.issues.${issue.code}`, { value: issue.value })}
            </Badge>
            {issue.suggestion ? (
              <Button variant="ghost" size="sm" onClick={() => onQuickFix(row, issue)}>
                {t('import.review.actions.useName', { name: issue.suggestion })}
              </Button>
            ) : null}
            {issue.code === ISSUE.REPORTS_TO_SELF ? (
              <Button variant="ghost" size="sm" onClick={() => onQuickFix(row, issue)}>
                {t('import.review.actions.removeManager')}
              </Button>
            ) : null}
          </li>
        ))}
    </ul>
  );
}

export default memo(StatusCell);
